"use client";

import { useUser } from "@clerk/tanstack-react-start";
import { useConsent } from "@policystack/react/consent";
import { PostHogProvider, usePostHog } from "@posthog/react";
import { getRouteApi } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { clientEnv } from "#/env";
import { posthogClientOptions } from "#/lib/posthog-config";

const rootRoute = getRouteApi("__root__");

function useAnalyticsConsent() {
  const { decisions, decidedAt } = useConsent();
  return {
    granted: decisions.analytics === true,
    decided: decidedAt !== null,
  };
}

function PostHogConsentBridge(): null {
  const posthog = usePostHog();
  const { granted, decided } = useAnalyticsConsent();

  useEffect(() => {
    if (granted) {
      if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing();
      return;
    }
    if (decided && !posthog.has_opted_out_capturing()) {
      posthog.opt_out_capturing();
    }
  }, [granted, decided, posthog]);

  return null;
}

function PostHogIdentify(): null {
  const posthog = usePostHog();
  const { session } = rootRoute.useLoaderData();
  const { isSignedIn, isLoaded, user } = useUser();
  const { granted } = useAnalyticsConsent();

  useEffect(() => {
    if (!isLoaded) return;

    if (!granted) {
      posthog.reset();
      return;
    }

    posthog.identify(
      session.playerId,
      isSignedIn && user
        ? {
            email: user.primaryEmailAddress?.emailAddress,
            name: user.fullName ?? undefined,
          }
        : undefined,
    );
  }, [granted, isLoaded, isSignedIn, posthog, session.playerId, user]);

  return null;
}

export function PostHogAnalytics({ children }: { children: ReactNode }) {
  return (
    <PostHogProvider apiKey={clientEnv.VITE_POSTHOG_PROJECT_TOKEN} options={posthogClientOptions()}>
      <PostHogConsentBridge />
      <PostHogIdentify />
      {children}
    </PostHogProvider>
  );
}
